import { Fragment, ReactFragment } from 'react';
import type { ReactChild, ReactText } from 'react';

import { TranslationPack } from './i18n';

type TranslationTemplate = string | Record<Intl.LDMLPluralRule, string>;

/**
 * Interface for advanced translation options, like plural forms.
 */
export interface TranslationOptions {
  /**
   * Value that determines which plural form will be chosen during translation.
   * @default 0
   */
  count?: number;
  // Descriminator for pluralization type: 'cardinal' or 'ordinal'.
  pluralType?: Intl.PluralRuleType;
}

/**
 * Function that takes translation key with optional tokens and translation options to drive the translation process.
 * Returns translated string with tokens inserted at defined slots.
 */
export interface TranslationFunction<T extends Record<string, TranslationTemplate>> {
  (literal: keyof T): string;
  (literal: keyof T, tokens: ReactText[], options?: TranslationOptions): string;
  (literal: keyof T, tokens: ReactChild[], options?: TranslationOptions): ReactFragment[];
}

/**
 * Function returns a function capable to translate tokens based on given translation pack and given locale.
 * The passed translation object should be correlated to given locale.
 * @param translation a translation pack (might be default or completely/partially overridden).
 * @param locale locale as BCP 47 language tag. Used for pluralization rules.
 * @returns
 */
const translatorFor = <T extends Record<string, TranslationTemplate>>(
  translation: T,
  locale: string
): TranslationFunction<T> => {
  const cardinalRules = new Intl.PluralRules(locale);
  const ordinalRules = new Intl.PluralRules(locale, { type: 'ordinal' });
  return ((
    literal: keyof TranslationPack,
    tokens: (ReactChild | ReactText)[] = [],
    options?: TranslationOptions
  ): string | ReactFragment[] => {
    let template: TranslationTemplate = translation[literal];
    if (!template) return `!unknown key: ${literal}`;
    const result: ReactChild[] = [];
    if (typeof template === 'object') {
      const type = (options?.pluralType === 'ordinal' ? ordinalRules : cardinalRules).select(
        options?.count || 0
      );
      template = template[type];
    }
    template.split(/\{(\d+)\}/g).forEach((chunk, index) => {
      const part = index % 2 ? tokens[Number(chunk)] : chunk;
      if (part !== undefined && part !== '') result.push(part);
    });

    if (result.every(part => typeof part === 'string' || typeof part === 'number'))
      return result.join('').trim();

    // eslint-disable-next-line react/no-array-index-key
    return result.map((part, i) => <Fragment key={i}>{part}</Fragment>);
  }) as TranslationFunction<T>;
};

export default translatorFor;
